import { Entity, generateEntityId } from './entity';
import { WA_PHYSICS, type Vector2 } from '../core/constants';

export type GraveState = 'fresh' | 'damaged' | 'crumbling';

export class Grave implements Entity {
  id: string;
  position: Vector2;
  velocity: Vector2 = { x: 0, y: 0 };
  active = true;

  health = 25;
  maxHealth = 25;
  state: GraveState = 'fresh';
  isGrounded = false;

  explosionRadius = 25;
  maxDamage = 25;

  onExplode?: (x: number, y: number) => void;

  constructor(x: number, y: number) {
    this.id = generateEntityId('grave');
    this.position = { x, y };
  }

  update(deltaTime: number): void {
    if (!this.active) return;

    if (!this.isGrounded) {
      this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime;
      this.position.x += this.velocity.x * deltaTime;
      this.position.y += this.velocity.y * deltaTime;
    }
  }

  applyTerrainPhysics(terrain: { isSolid(x: number, y: number): boolean }): void {
    if (!this.active) return;

    if (terrain.isSolid(this.position.x, this.position.y + 1)) {
      while (terrain.isSolid(this.position.x, this.position.y)) {
        this.position.y -= 1;
      }
      this.velocity.y = 0;
      this.isGrounded = true;
    } else {
      this.isGrounded = false;
    }
  }

  takeDamage(amount: number, pushX: number, pushY: number): void {
    if (!this.active) return;

    this.health -= amount;

    this.velocity.x += pushX * 0.3;
    this.velocity.y += pushY * 0.3;
    this.isGrounded = false;

    this.updateState();

    if (this.health <= 0) {
      this.explode();
    }
  }

  private updateState(): void {
    const healthPercent = this.health / this.maxHealth;
    if (healthPercent > 0.6) {
      this.state = 'fresh';
    } else if (healthPercent > 0.3) {
      this.state = 'damaged';
    } else {
      this.state = 'crumbling';
    }
  }

  explode(): void {
    if (!this.active) return;
    this.active = false;
    this.onExplode?.(this.position.x, this.position.y);
  }

  destroy(): void {
    this.active = false;
  }

  getBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.position.x - 12,
      y: this.position.y - 24,
      width: 24,
      height: 32,
    };
  }
}
